import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { isAdmin } from '../../features/auth/authSlice';
import api from '../../utils/axiosConfig';
import { Navigate } from 'react-router-dom';
import {
    ChartBarIcon,
    UserIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    EyeIcon
} from '@heroicons/react/24/outline';

interface AnalyticsData {
    users: {
        total: number;
        active: number;
        newThisMonth: number;
        growth: number;
    };
    companies: {
        total: number;
        active: number;
        newThisMonth: number;
        growth: number;
    };
    jobs: {
        total: number;
        active: number;
        completed: number;
        growth: number;
    };
    revenue: {
        total: number;
        thisMonth: number;
        growth: number;
    };
    recentActivity: Array<{
        id: string;
        type: string;
        description: string;
        timestamp: string;
        user?: string;
    }>;
    chartData: {
        labels: string[];
        users: number[];
        companies: number[];
        jobs: number[];
        revenue: number[];
    };
}

const Analytics: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('30d');

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            // Fetch all data in parallel using correct API endpoints
            const [usersResponse, companiesResponse, tasksResponse, adminStatsResponse, notificationsResponse] = await Promise.all([
                api.get('/users'),
                api.get('/companies'),
                api.get('/tasks'),
                api.get('/admin/dashboard-stats'),
                api.get('/notifications')
            ]);

            const users = usersResponse.data.data || [];
            const companies = companiesResponse.data.data || [];
            const tasks = tasksResponse.data.data?.tasks || [];
            const adminStats = adminStatsResponse.data.data || {};
            const notifications = notificationsResponse.data.data || [];

            // Debug logging
            console.log('Analytics API Responses:', {
                users: users.length,
                companies: companies.length,
                tasks: tasks.length,
                adminStats,
                notifications: notifications.length
            });

            // Calculate current month stats
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();

            // Use admin stats for accurate counts
            const totalUsers = adminStats.users?.total || users.length;
            const activeUsers = adminStats.users?.active || users.filter((user: any) => user.isActive).length;
            const newUsersThisMonth = users.filter((user: any) => {
                const userDate = new Date(user.createdAt);
                return userDate.getMonth() === currentMonth && userDate.getFullYear() === currentYear;
            }).length;

            const totalCompanies = adminStats.companies?.total || companies.length;
            const activeCompanies = adminStats.companies?.active || companies.filter((company: any) => company.isActive).length;
            const newCompaniesThisMonth = companies.filter((company: any) => {
                const companyDate = new Date(company.createdAt);
                return companyDate.getMonth() === currentMonth && companyDate.getFullYear() === currentYear;
            }).length;

            const totalTasks = adminStats.tasks?.total || tasks.length;
            const activeTasks = adminStats.tasks?.active || tasks.filter((task: any) => task.status === 'active').length;
            const completedTasks = adminStats.tasks?.completed || tasks.filter((task: any) => task.status === 'completed').length;

            // Calculate revenue from tasks (simplified calculation)
            const totalRevenue = tasks.reduce((sum: number, task: any) => sum + (task.totalBudget || 0), 0);
            const thisMonthRevenue = tasks.filter((task: any) => {
                const taskDate = new Date(task.createdAt);
                return taskDate.getMonth() === currentMonth && taskDate.getFullYear() === currentYear;
            }).reduce((sum: number, task: any) => sum + (task.totalBudget || 0), 0);

            // Calculate growth percentages (simplified calculation)
            const userGrowth = totalUsers > 0 ? ((newUsersThisMonth / totalUsers) * 100) : 0;
            const companyGrowth = totalCompanies > 0 ? ((newCompaniesThisMonth / totalCompanies) * 100) : 0;
            const taskGrowth = totalTasks > 0 ? ((activeTasks / totalTasks) * 100) : 0;
            const revenueGrowth = totalRevenue > 0 ? ((thisMonthRevenue / totalRevenue) * 100) : 0;

            // Get recent activity from notifications
            const recentActivity = notifications.slice(0, 4).map((notification: any) => ({
                id: notification._id,
                type: notification.type,
                description: notification.message,
                timestamp: notification.createdAt,
                user: notification.user ? `${notification.user.firstName} ${notification.user.lastName}` : 'System'
            }));

            // Generate chart data for last 6 months
            const chartLabels = [];
            const chartUsers = [];
            const chartCompanies = [];
            const chartJobs = [];
            const chartRevenue = [];

            for (let i = 5; i >= 0; i--) {
                const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const monthName = date.toLocaleDateString('en-US', { month: 'short' });
                chartLabels.push(monthName);

                // Count users created in this month
                const usersInMonth = users.filter((user: any) => {
                    const userDate = new Date(user.createdAt);
                    return userDate.getMonth() === date.getMonth() && userDate.getFullYear() === date.getFullYear();
                }).length;
                chartUsers.push(usersInMonth);

                // Count companies created in this month
                const companiesInMonth = companies.filter((company: any) => {
                    const companyDate = new Date(company.createdAt);
                    return companyDate.getMonth() === date.getMonth() && companyDate.getFullYear() === date.getFullYear();
                }).length;
                chartCompanies.push(companiesInMonth);

                // Count tasks created in this month
                const tasksInMonth = tasks.filter((task: any) => {
                    const taskDate = new Date(task.createdAt);
                    return taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
                }).length;
                chartJobs.push(tasksInMonth);

                // Calculate revenue for this month from tasks
                const revenueInMonth = tasks.filter((task: any) => {
                    const taskDate = new Date(task.createdAt);
                    return taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
                }).reduce((sum: number, task: any) => sum + (task.totalBudget || 0), 0);
                chartRevenue.push(revenueInMonth);
            }

            const analyticsData: AnalyticsData = {
                users: {
                    total: totalUsers,
                    active: activeUsers,
                    newThisMonth: newUsersThisMonth,
                    growth: userGrowth
                },
                companies: {
                    total: totalCompanies,
                    active: activeCompanies,
                    newThisMonth: newCompaniesThisMonth,
                    growth: companyGrowth
                },
                jobs: {
                    total: totalTasks,
                    active: activeTasks,
                    completed: completedTasks,
                    growth: taskGrowth
                },
                revenue: {
                    total: totalRevenue,
                    thisMonth: thisMonthRevenue,
                    growth: revenueGrowth
                },
                recentActivity,
                chartData: {
                    labels: chartLabels,
                    users: chartUsers,
                    companies: chartCompanies,
                    jobs: chartJobs,
                    revenue: chartRevenue
                }
            };

            setAnalytics(analyticsData);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch analytics',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [timeRange]);

    // Ensure only admin can access this page
    if (!user || !isAdmin(user)) {
        return <Navigate to="/admin" replace />;
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActivityIcon = (type: string) => {
        switch (type) {
            case 'user_registration':
                return <UserIcon className="h-5 w-5 text-green-500" />;
            case 'job_posted':
                return <BriefcaseIcon className="h-5 w-5 text-blue-500" />;
            case 'company_verified':
                return <BuildingOfficeIcon className="h-5 w-5 text-purple-500" />;
            case 'payment_completed':
                return <CurrencyDollarIcon className="h-5 w-5 text-yellow-500" />;
            default:
                return <EyeIcon className="h-5 w-5 text-gray-500" />;
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center">
                    <p className="text-gray-500 dark:text-gray-400">No analytics data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Overview of platform performance and metrics
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0">
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                            <option value="1y">Last year</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Users */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.users.total.toLocaleString()}</p>
                            <div className="flex items-center mt-2">
                                {analytics.users.growth > 0 ? (
                                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <span className={`text-sm ${analytics.users.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {analytics.users.growth > 0 ? '+' : ''}{analytics.users.growth}%
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                            </div>
                        </div>
                        <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <UserIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                </div>

                {/* Companies */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Companies</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.companies.total}</p>
                            <div className="flex items-center mt-2">
                                {analytics.companies.growth > 0 ? (
                                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <span className={`text-sm ${analytics.companies.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {analytics.companies.growth > 0 ? '+' : ''}{analytics.companies.growth}%
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                            </div>
                        </div>
                        <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                            <BuildingOfficeIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                        </div>
                    </div>
                </div>

                {/* Jobs */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Jobs</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.jobs.total}</p>
                            <div className="flex items-center mt-2">
                                {analytics.jobs.growth > 0 ? (
                                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <span className={`text-sm ${analytics.jobs.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {analytics.jobs.growth > 0 ? '+' : ''}{analytics.jobs.growth}%
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                            </div>
                        </div>
                        <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                            <BriefcaseIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                        </div>
                    </div>
                </div>

                {/* Revenue */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(analytics.revenue.total)}</p>
                            <div className="flex items-center mt-2">
                                {analytics.revenue.growth > 0 ? (
                                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <span className={`text-sm ${analytics.revenue.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {analytics.revenue.growth > 0 ? '+' : ''}{analytics.revenue.growth}%
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
                            </div>
                        </div>
                        <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                            <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* User Growth Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Growth Trend</h3>
                    <div className="h-64 flex items-center justify-center">
                        <div className="w-full">
                            <div className="flex items-end justify-between h-48 space-x-2">
                                {analytics.chartData.users.map((value, index) => (
                                    <div key={index} className="flex flex-col items-center flex-1">
                                        <div
                                            className="bg-blue-500 rounded-t w-full mb-2 transition-all duration-300 hover:bg-blue-600"
                                            style={{ height: `${Math.max((value / Math.max(...analytics.chartData.users)) * 150, 10)}px` }}
                                            title={`${analytics.chartData.labels[index]}: ${value} users`}
                                        ></div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                            {analytics.chartData.labels[index]}
                                        </span>
                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                            {value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Task Completion Rate */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Task Activity Trend</h3>
                    <div className="h-64 flex items-center justify-center">
                        <div className="w-full">
                            <div className="flex items-end justify-between h-48 space-x-2">
                                {analytics.chartData.jobs.map((value, index) => (
                                    <div key={index} className="flex flex-col items-center flex-1">
                                        <div
                                            className="bg-green-500 rounded-t w-full mb-2 transition-all duration-300 hover:bg-green-600"
                                            style={{ height: `${Math.max((value / Math.max(...analytics.chartData.jobs)) * 150, 10)}px` }}
                                            title={`${analytics.chartData.labels[index]}: ${value} tasks`}
                                        ></div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                            {analytics.chartData.labels[index]}
                                        </span>
                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                            {value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Statistics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* Platform Overview */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Platform Overview</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Active Users</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{analytics.users.active}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Active Companies</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{analytics.companies.active}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Active Tasks</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{analytics.jobs.active}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Completed Tasks</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{analytics.jobs.completed}</span>
                        </div>
                    </div>
                </div>

                {/* Monthly Growth */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">This Month</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">New Users</span>
                            <span className="font-semibold text-green-600">{analytics.users.newThisMonth}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">New Companies</span>
                            <span className="font-semibold text-green-600">{analytics.companies.newThisMonth}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Revenue</span>
                            <span className="font-semibold text-green-600">{formatCurrency(analytics.revenue.thisMonth)}</span>
                        </div>
                    </div>
                </div>

                {/* Performance Metrics */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Task Completion Rate</span>
                            <span className="font-semibold text-blue-600">
                                {analytics.jobs.total > 0 ? Math.round((analytics.jobs.completed / analytics.jobs.total) * 100) : 0}%
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">User Growth Rate</span>
                            <span className={`font-semibold ${analytics.users.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {analytics.users.growth > 0 ? '+' : ''}{analytics.users.growth.toFixed(1)}%
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Company Growth Rate</span>
                            <span className={`font-semibold ${analytics.companies.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {analytics.companies.growth > 0 ? '+' : ''}{analytics.companies.growth.toFixed(1)}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
                <div className="space-y-4">
                    {analytics.recentActivity.length > 0 ? (
                        analytics.recentActivity.map((activity) => (
                            <div key={activity.id} className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div className="flex-shrink-0">
                                    {getActivityIcon(activity.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {activity.description}
                                    </p>
                                    {activity.user && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            by {activity.user}
                                        </p>
                                    )}
                                </div>
                                <div className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(activity.timestamp)}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-8">
                            <EyeIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400">No recent activity</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Analytics;
